import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ListPrefs, UpdateMyPreferences, UserPreferences } from '@mnc/contracts';
import { api } from './api';

const KEY = ['me', 'preferences'];

/** Server-side user preferences (dashboard layout, saved views, list settings) that follow the user across devices. */
export function usePreferences() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: KEY, queryFn: () => api.get<UserPreferences>('/api/me/preferences'), staleTime: 60_000 });
  const mutation = useMutation({
    mutationFn: (body: UpdateMyPreferences) => api.patch<UserPreferences>('/api/me/preferences', body),
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
  return { prefs: query.data, loading: query.isLoading, update: mutation.mutateAsync };
}

/** Remembered settings of one list (page size, sort, columns): read from preferences, written through on change. */
export function useListPrefs(listKey: string, defaults: ListPrefs = {}) {
  const { prefs, update } = usePreferences();
  const current: ListPrefs = { ...defaults, ...(prefs?.lists[listKey] ?? {}) };
  const set = useCallback(
    (patch: ListPrefs) => {
      void update({ lists: { [listKey]: { ...(prefs?.lists[listKey] ?? {}), ...patch } } }).catch(() => undefined);
    },
    [listKey, prefs, update],
  );
  return { listPrefs: current, setListPrefs: set, views: prefs?.views[listKey] ?? [] };
}
